import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInAnonymously, GoogleAuthProvider, signInWithPopup, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from "firebase/auth";
import { doc, setDoc, getDoc, collection, query, where, orderBy, getDocs, updateDoc, serverTimestamp, getCountFromServer, runTransaction, onSnapshot } from "firebase/firestore";
import { auth, db, appId } from '../config/firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let unsubscribeUserDoc = null;

        const authCheck = onAuthStateChanged(auth, (currentUser) => {
            if (unsubscribeUserDoc) {
                unsubscribeUserDoc();
                unsubscribeUserDoc = null;
            }

            if (currentUser) {
                const userDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}`);

                unsubscribeUserDoc = onSnapshot(userDocRef, async (userDoc) => {
                    let userData = {};

                    if (!userDoc.exists()) {
                        // Handle new user creation delay or race condition if needed, 
                        // but usually if we are here, we might want to wait or init.
                        // For now, if doc doesn't exist yet (registration in progress), we might wait?
                        // Actually, emailAuth creates the doc. 
                        // If it's a google login, we might need to create it here if it doesn't exist?
                        // The original code created it.

                        // Trial Granting Logic REMOVED
                        let pendingTrialLicense = null;

                        // Ambil role dari session storage jika ada (untuk login Google pertama kali)
                        const storedRole = sessionStorage.getItem('reg_role');
                        const storedNisn = sessionStorage.getItem('reg_nisn');

                        // Default ke teacher hanya jika tidak ada stored role
                        // Jika login biasa via emailAuth, role sudah diset via emailAuth params (tapi emailAuth handle create doc sendiri biasanya?)
                        // Oh, emailAuth creates the user credential, then calls setDoc IMMEDIATELY. 
                        // So this onSnapshot might run AFTER emailAuth's setDoc.
                        // If doc is already created by emailAuth (setDoc), userDoc.exists() will be TRUE.
                        // So this block is primarily for Google Login (Provider) which doesn't create user doc automatically.

                        const initRole = storedRole || 'teacher';

                        userData = {
                            displayName: currentUser.displayName || 'Pengguna Baru',
                            email: currentUser.email,
                            createdAt: serverTimestamp(),
                            isPremium: false,
                            role: initRole,
                            userType: initRole,
                            subscriptionStatus: 'inactive',
                            isTrial: false,
                            subscriptionEndDate: null,
                            pendingTrialLicense: pendingTrialLicense
                        };

                        if (initRole === 'student' && storedNisn) {
                            userData.nisn = storedNisn;
                        }

                        // Bersihkan session storage
                        sessionStorage.removeItem('reg_role');
                        sessionStorage.removeItem('reg_nisn');

                        try {
                            await setDoc(userDocRef, userData);
                        } catch (error) {
                            console.error("Error creating user doc:", error);
                        }
                    } else {
                        userData = userDoc.data();
                        // Security check
                        if (userData.isDisabled || userData.status === 'deleted') {
                            await signOut(auth);
                            setUser(null);
                            setIsLoading(false);
                            return;
                        }
                    }

                    // Auto-switch to active period logic (Simplified for snapshot)
                    // We only do this check once or if data is missing
                    if (!userData.currentAcademicPeriod) {
                        // ... (keep existing period logic if needed, but maybe skip for now to avoid complexity in this step? 
                        // The original logic had async period fetching. 
                        // Putting async logic inside onSnapshot callback is fine.)
                        try {
                            const periodsRef = collection(db, `artifacts/${appId}/academicPeriods`);
                            const periodsQuery = query(
                                periodsRef,
                                where('status', '==', 'active'),
                                where('isActive', '==', true),
                                orderBy('createdAt', 'desc')
                            );

                            const periodsSnapshot = await getDocs(periodsQuery);
                            if (!periodsSnapshot.empty) {
                                const activePeriod = periodsSnapshot.docs[0];
                                await updateDoc(userDocRef, {
                                    currentAcademicPeriod: activePeriod.id,
                                    updatedAt: serverTimestamp()
                                });
                                // The update will trigger onSnapshot again, so we don't need to manually update userData here
                                return;
                            }
                        } catch (error) {
                            // ignore
                        }
                    }

                    // Construct User Object
                    const displayNameFromFirestore = userData.nama || userData.studentName || userData.displayName;
                    const isManualPremium = userData.isPremium === true;

                    const licenseStatus = userData.subscriptionStatus;
                    const licenseEndDate = userData.subscriptionEndDate?.toDate ? userData.subscriptionEndDate.toDate() : null;
                    const isLicenseValid = (licenseStatus === 'active' || licenseStatus === 'trial') &&
                        (licenseEndDate ? licenseEndDate > new Date() : false);

                    const effectivePremium = isManualPremium || isLicenseValid;

                    const fullUser = {
                        ...currentUser,
                        ...userData,
                        isManualPremium: isManualPremium,
                        isLicenseValid: isLicenseValid,
                        isPremium: effectivePremium,
                        displayName: displayNameFromFirestore || currentUser.displayName || 'Pengguna Baru'
                    };

                    setUser(fullUser);
                    setIsLoading(false);
                }, (error) => {
                    console.error("User doc snapshot error:", error);
                    setIsLoading(false);
                });

            } else {
                setUser(null);
                setIsLoading(false);
            }
        });

        return () => {
            authCheck();
            if (unsubscribeUserDoc) unsubscribeUserDoc();
        };
    }, []);

    const login = async (provider, role = 'teacher', nisn = '') => {
        try {
            if (provider === 'google') {
                // Simpan role dan nisn sementara untuk diambil setelah redirect/popup login sukses
                sessionStorage.setItem('reg_role', role);
                if (role === 'student' && nisn) {
                    sessionStorage.setItem('reg_nisn', nisn);
                }

                const googleProvider = new GoogleAuthProvider();
                await signInWithPopup(auth, googleProvider);
            } else {
                await signInAnonymously(auth);
            }
        } catch (error) {
            // Login failed silently
            console.error("Login Error:", error);
        }
    };


    const activateLicense = async (code) => {
        if (!user || !code) throw new Error("User atau kode tidak valid");
        const activationCode = code.trim().toUpperCase();

        await runTransaction(db, async (transaction) => {
            // 1. Find the license by code
            const licensesRef = collection(db, `artifacts/${appId}/licenses`);
            const q = query(licensesRef, where('code', '==', activationCode), where('status', '==', 'active'));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                throw new Error("Kode lisensi tidak valid atau sudah digunakan.");
            }

            const licenseDoc = querySnapshot.docs[0];
            const licenseData = licenseDoc.data();
            const licenseRef = doc(db, `artifacts/${appId}/licenses`, licenseDoc.id);

            // 2. Calculate new end date
            const DurationMonths = parseFloat(licenseData.durationMonths); // Support decimals for trial (0.25)
            let newEndDate = new Date();

            // If user already has active subscription, extend it
            if (user.subscriptionEndDate && user.subscriptionEndDate.toDate && user.subscriptionEndDate.toDate() > new Date()) {
                newEndDate = user.subscriptionEndDate.toDate();
            }

            // Add duration (approximate for months, robust for days if < 1 month)
            if (DurationMonths < 1) {
                // Assume days (e.g. 0.25 month ~= 1 week, roughly. Better to use days in future but sticking to month logic)
                // Actually, let's fix the logic: 1 month = 30 days.
                const daysToAdd = Math.round(DurationMonths * 30);
                newEndDate.setDate(newEndDate.getDate() + daysToAdd);
            } else {
                newEndDate.setMonth(newEndDate.getMonth() + parseInt(DurationMonths));
            }

            // 3. Update License Status
            transaction.update(licenseRef, {
                status: 'used',
                usedBy: user.uid,
                usedByName: user.displayName || 'User',
                usedAt: serverTimestamp()
            });

            // 4. Update User Subscription
            const userRef = doc(db, `artifacts/${appId}/users/${user.uid}`);
            transaction.update(userRef, {
                subscriptionStatus: 'active',
                subscriptionEndDate: newEndDate,
                isTrial: licenseData.type === 'trial',
                isPremium: true,
                activeLicenseCode: activationCode, // Save license code
                // Remove pendingTrialLicense if it matches
                ...(user.pendingTrialLicense === activationCode ? { pendingTrialLicense: null } : {})
            });
        });

        // Trigger reload to refresh context state if needed
        // In a real app, onAuthStateChanged should pick this up, but a reload ensures safety
        return true;
    };

    const emailAuth = async (email, password, isRegister, role = 'teacher', nisn = '') => {
        if (isRegister) {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const displayName = email.split('@')[0];
            await updateProfile(userCredential.user, { displayName: displayName });

            // Trial Granting Logic REMOVED


            // Prepare User Data
            const userDocRef = doc(db, `artifacts/${appId}/users/${userCredential.user.uid}`);
            const userData = {
                displayName: displayName,
                email: email,
                createdAt: serverTimestamp(),
                isPremium: false,
                role: role,
                userType: role,
                subscriptionStatus: 'inactive', // Default inactive
                isTrial: false,
                subscriptionEndDate: null
            };

            // Trial License Logic REMOVED


            // Add role-specific data
            if (role === 'student' && nisn) {
                userData.nisn = nisn;
            }

            try {
                const settingsRef = doc(db, `artifacts/${appId}/settings/global`);
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists()) {
                    const settingsData = settingsSnap.data();
                    userData.namaSekolah = settingsData.namaSekolah || '';
                } else {
                    userData.namaSekolah = '';
                }
            } catch (_) {
                userData.namaSekolah = '';
            }

            await setDoc(userDocRef, userData);
        } else {
            await signInWithEmailAndPassword(auth, email, password);
        }
    };



    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    };

    const logout = () => signOut(auth);

    const managePassword = async (newPassword, oldPassword = null) => {
        if (!auth.currentUser) throw new Error("Tidak ada pengguna yang login.");

        const user = auth.currentUser;
        const providerId = user.providerData && user.providerData.length > 0 ? user.providerData[0].providerId : 'password';

        try {
            // Skenario 1: User Login via Password (Email/Pass)
            // Wajib re-auth dengan password lama untuk keamanan
            if (providerId === 'password' || user.providerData.some(p => p.providerId === 'password')) {
                if (!oldPassword) {
                    throw new Error("Password lama diperlukan untuk verifikasi keamanan.");
                }

                const credential = EmailAuthProvider.credential(user.email, oldPassword);
                await reauthenticateWithCredential(user, credential);
            }
            // Skenario 2: User Login via Google/Sosmed (Belum punya password)
            // Tidak butuh oldPassword, tapi mungkin butuh re-auth popup jika sesi expired
            else if (providerId === 'google.com') {
                // Opsional: Paksa re-auth popup jika error requires-recent-login muncul
                // Untuk sekarang coba langsung update
            }

            // Update Password
            await updatePassword(user, newPassword);
            return { success: true };

        } catch (error) {
            console.error("Error managing password:", error);
            if (error.code === 'auth/wrong-password') {
                throw new Error("Password lama yang Anda masukkan salah.");
            } else if (error.code === 'auth/requires-recent-login') {
                throw new Error("Sesi Anda telah kadaluarsa. Silakan logout dan login kembali sebelum mengganti password.");
            } else if (error.code === 'auth/weak-password') {
                throw new Error("Password terlalu lemah. Gunakan minimal 6 karakter.");
            }
            throw error;
        }
    };

    const value = { user, isLoading, login, emailAuth, logout, resetPassword, activateLicense, managePassword };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

